/**
 * BadgeArt — 15 custom inline SVG badges for Sudoku Academy achievements.
 *
 * Each badge is a composition:
 *   <BadgeFrame color={cat}>   (shared shield/ring with category gradient)
 *     <core insignia />        (unique SVG per achievement)
 *   </BadgeFrame>
 *
 * Consumers pass `achievementId` and `size`. The frame auto-greyscales when
 * `locked` so the lock UI in AchievementsPage keeps a consistent vocabulary.
 */
import React from 'react';

const CATEGORY_PALETTE = {
  Progress:    { a: '#34C759', b: '#1A9F3F', ring: '#34C75944' },
  Milestones:  { a: '#8B5A3C', b: '#0051B3', ring: '#8B5A3C44' },
  Speed:       { a: '#FF9F0A', b: '#CC7A00', ring: '#FF9F0A44' },
  Mastery:     { a: '#AF52DE', b: '#7B2CBF', ring: '#AF52DE44' },
  Streaks:     { a: '#FF3B30', b: '#B51F1F', ring: '#FF3B3044' },
};

function BadgeFrame({ category = 'Progress', locked = false, size = 72, children, gradId }) {
  const pal = CATEGORY_PALETTE[category] || CATEGORY_PALETTE.Progress;
  const fill = locked ? '#3C3C43' : `url(#${gradId})`;
  return (
    <svg viewBox="0 0 72 72" width={size} height={size} style={{ filter: locked ? 'grayscale(1)' : undefined }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={pal.a} />
          <stop offset="100%" stopColor={pal.b} />
        </linearGradient>
        <radialGradient id={`${gradId}-glow`} cx="0.5" cy="0.35" r="0.7">
          <stop offset="0%" stopColor="white" stopOpacity={locked ? 0 : 0.4} />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Outer ring */}
      <circle cx="36" cy="36" r="34" fill="none" stroke={pal.ring} strokeWidth="3" />
      {/* Shield/disc */}
      <circle cx="36" cy="36" r="29" fill={fill} />
      {/* Inner rim highlight */}
      <circle cx="36" cy="36" r="29" fill={`url(#${gradId}-glow)`} />
      {children}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────
// Unique insignia per achievement. Each returns SVG children
// rendered inside the 72x72 frame.
// ─────────────────────────────────────────────────────────

const INSIGNIA = {
  // Progress — green
  first_solve: () => (
    <>
      {/* sprout + checkmark */}
      <path d="M36 48 Q30 40 30 34 Q36 38 36 44" fill="white" opacity="0.85" />
      <path d="M36 48 Q42 40 42 34 Q36 38 36 44" fill="white" opacity="0.85" />
      <path d="M28 50 L33 55 L46 42" stroke="white" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  dedicated: () => (
    <>
      {/* flame silhouette with 25 */}
      <path d="M36 22 Q42 30 42 38 Q42 50 36 52 Q30 50 30 38 Q30 30 36 22 Z" fill="white" opacity="0.3" />
      <text x="36" y="44" textAnchor="middle" fontSize="15" fontWeight="800" fill="white" fontFamily="system-ui">25</text>
    </>
  ),
  century: () => (
    <>
      {/* laurel + 100 */}
      <path d="M18 40 Q22 48 28 50" stroke="white" strokeWidth="2" fill="none" opacity="0.6" />
      <path d="M54 40 Q50 48 44 50" stroke="white" strokeWidth="2" fill="none" opacity="0.6" />
      <path d="M22 38 Q20 34 24 32 M26 42 Q24 38 28 36 M48 38 Q50 34 46 32 M46 42 Q48 38 44 36" stroke="white" strokeWidth="1.5" fill="none" opacity="0.6" />
      <text x="36" y="42" textAnchor="middle" fontSize="14" fontWeight="800" fill="white" fontFamily="system-ui">100</text>
    </>
  ),

  // Milestones — blue
  first_medium: () => (
    <>
      {/* target rings with arrow */}
      <circle cx="36" cy="36" r="13" fill="none" stroke="white" strokeWidth="1.5" opacity="0.5" />
      <circle cx="36" cy="36" r="8" fill="none" stroke="white" strokeWidth="1.5" opacity="0.7" />
      <circle cx="36" cy="36" r="3" fill="white" />
      <path d="M22 22 L36 36" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M22 22 L24 28 L28 24 Z" fill="white" />
    </>
  ),
  first_advanced: () => (
    <>
      {/* feather / wing */}
      <path d="M24 50 Q30 36 46 22 Q48 30 46 38 Q42 48 30 52 Z" fill="white" opacity="0.85" />
      <path d="M26 48 L46 28" stroke="#0051B3" strokeWidth="1" opacity="0.6" />
    </>
  ),
  first_expert: () => (
    <>
      {/* broken chain links */}
      <rect x="20" y="32" width="14" height="8" rx="4" fill="none" stroke="white" strokeWidth="2.5" />
      <rect x="38" y="32" width="14" height="8" rx="4" fill="none" stroke="white" strokeWidth="2.5" />
      <path d="M34 36 L38 36" stroke="white" strokeWidth="2.5" />
      <path d="M30 26 L26 22 M42 46 L46 50" stroke="white" strokeWidth="2" opacity="0.5" strokeLinecap="round" />
    </>
  ),
  curriculum_complete: () => (
    <>
      {/* grad cap */}
      <path d="M18 36 L36 28 L54 36 L36 44 Z" fill="white" />
      <path d="M54 36 L54 46 Q54 48 52 48" fill="none" stroke="white" strokeWidth="2" />
      <path d="M24 40 L24 48 Q36 52 48 48 L48 40" fill="none" stroke="white" strokeWidth="2" />
      <circle cx="54" cy="48" r="2" fill="white" />
    </>
  ),

  // Speed — amber
  quick_thinker: () => (
    <>
      {/* lightning bolt */}
      <path d="M38 20 L26 42 L34 42 L32 52 L46 30 L38 30 Z" fill="white" />
    </>
  ),
  swift_solver: () => (
    <>
      {/* stopwatch */}
      <circle cx="36" cy="38" r="14" fill="none" stroke="white" strokeWidth="2.5" />
      <rect x="32" y="20" width="8" height="4" fill="white" />
      <rect x="35" y="18" width="2" height="2" fill="white" />
      <path d="M36 38 L36 30" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <path d="M36 38 L44 40" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <circle cx="36" cy="38" r="1.5" fill="white" />
    </>
  ),

  // Mastery — purple
  pentagon: () => (
    <>
      {/* pentagon + 5 stars */}
      <polygon points="36,18 52,30 46,50 26,50 20,30" fill="none" stroke="white" strokeWidth="2" />
      {[[36,22],[48,31],[44,46],[28,46],[24,31]].map(([x,y], i) => (
        <circle key={i} cx={x} cy={y} r="2" fill="white" />
      ))}
    </>
  ),
  decathlon: () => (
    <>
      {/* 10 dots in crown arc */}
      {Array.from({ length: 10 }).map((_, i) => {
        const angle = (-140 + (280 / 9) * i) * Math.PI / 180;
        const x = 36 + Math.cos(angle) * 18;
        const y = 36 + Math.sin(angle) * 18;
        return <circle key={i} cx={x} cy={y} r="2.5" fill="white" opacity={0.5 + 0.05 * i} />;
      })}
      <text x="36" y="42" textAnchor="middle" fontSize="13" fontWeight="800" fill="white" fontFamily="system-ui">10</text>
    </>
  ),
  grandmaster: () => (
    <>
      {/* crown */}
      <path d="M18 48 L22 28 L30 38 L36 24 L42 38 L50 28 L54 48 Z" fill="white" />
      <rect x="18" y="48" width="36" height="4" fill="white" />
      <circle cx="22" cy="28" r="2" fill="#AF52DE" />
      <circle cx="36" cy="24" r="2.5" fill="#AF52DE" />
      <circle cx="50" cy="28" r="2" fill="#AF52DE" />
    </>
  ),

  // Streaks — red
  streak_3: () => (
    <>
      {/* flame */}
      <path d="M36 20 Q46 30 44 44 Q42 52 36 54 Q30 52 28 44 Q26 30 36 20 Z" fill="white" opacity="0.9" />
      <path d="M36 34 Q40 40 38 46 Q36 50 34 46 Q32 40 36 34 Z" fill="#FF3B30" opacity="0.5" />
      <text x="36" y="48" textAnchor="middle" fontSize="12" fontWeight="800" fill="#B51F1F" fontFamily="system-ui">3</text>
    </>
  ),
  streak_7: () => (
    <>
      {/* calendar with 7 */}
      <rect x="20" y="24" width="32" height="28" rx="3" fill="white" opacity="0.92" />
      <rect x="20" y="24" width="32" height="8" rx="3" fill="#B51F1F" />
      <rect x="24" y="20" width="3" height="8" fill="#B51F1F" />
      <rect x="45" y="20" width="3" height="8" fill="#B51F1F" />
      <text x="36" y="47" textAnchor="middle" fontSize="14" fontWeight="800" fill="#FF3B30" fontFamily="system-ui">7</text>
    </>
  ),
  streak_30: () => (
    <>
      {/* moon phases semicircle + 30 */}
      <path d="M36 20 Q50 30 52 42" stroke="white" strokeWidth="2" fill="none" opacity="0.5" />
      <circle cx="22" cy="34" r="3" fill="white" opacity="0.3" />
      <path d="M29 28 A3 3 0 1 1 29 32 A1.5 1.5 0 0 0 29 28" fill="white" opacity="0.6" />
      <circle cx="36" cy="24" r="3" fill="white" opacity="0.9" />
      <path d="M43 28 A3 3 0 1 0 43 32 A1.5 1.5 0 0 1 43 28" fill="white" opacity="0.6" />
      <circle cx="50" cy="34" r="3" fill="white" opacity="0.3" />
      <text x="36" y="50" textAnchor="middle" fontSize="14" fontWeight="800" fill="white" fontFamily="system-ui">30</text>
    </>
  ),
};

export default function BadgeArt({ achievementId, category, locked = false, size = 72 }) {
  const Insignia = INSIGNIA[achievementId];
  const gradId = `grad-${achievementId}-${locked ? 'l' : 'u'}`;
  return (
    <BadgeFrame category={category} locked={locked} size={size} gradId={gradId}>
      {Insignia ? <Insignia /> : null}
    </BadgeFrame>
  );
}
